import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserSearch } from "lucide-react";
import { RemoveFromPayrollDialog } from "./RemoveFromPayrollDialog";
import { ReassignAttendanceDialog } from "./ReassignAttendanceDialog";
import { DeleteEmployeeDialog } from "./DeleteEmployeeDialog";

const FILTERS = ["ALL", "ACTIVE", "FORMER", "DUPLICATE", "DELETED", "SALARY_MISSING", "ATTENDANCE_MISMATCH", "PAYROLL_MISMATCH"];

export function EmployeeEligibilityPanel({ session, month }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [removals, setRemovals] = useState([]);
  const [rm, setRm] = useState(null);
  const [ra, setRa] = useState(null);
  const [del, setDel] = useState(null);
  const token = session.token;

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.post("/payroll-corrections/identity-review", { token, month }),
        api.post("/payroll-corrections/removals", { token, month })]);
      setData(a.data); setRemovals(b.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const restore = async (r) => {
    try { await api.post("/payroll-corrections/restore", { token, month, employee_id: r.employee_id, center: r.center });
      toast.success("Restored to payroll"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const items = (data?.items || []).filter((i) => filter === "ALL" || i.categories.includes(filter));
  return (
    <Card data-testid="eligibility-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><UserSearch className="w-4 h-4" />Review Employee Eligibility · {month}</CardTitle>
        <Button size="sm" variant="outline" onClick={load} data-testid="eligibility-load-btn">Review Employee Eligibility</Button>
      </CardHeader>
      {data && (
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <Button key={f} size="sm" className="h-7" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}
                data-testid={`eligibility-filter-${f}`}>{f.replace(/_/g, " ")} {f === "ALL" ? data.items.length : data.counts[f] || 0}</Button>))}
          </div>
          {removals.length > 0 && (
            <div className="text-xs border rounded p-2" data-testid="eligibility-removals">
              <b>Removed from this payroll by SA:</b>
              {removals.map((r) => (
                <div key={`${r.employee_id}-${r.center}`} className="flex justify-between items-center">
                  <span>{r.employee_name} · {r.employee_id} · {r.center} — {r.reason} ({r.by})</span>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => restore(r)} data-testid={`eligibility-restore-${r.employee_id}`}>Restore</Button>
                </div>))}
            </div>)}
          <div className="border rounded overflow-auto max-h-[55vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 text-left"><tr>
                {["Employee", "Employee ID", "Centre", "Payable Days", "Categories", "Details", "Actions"].map((h) => <th key={h} className="p-1.5">{h}</th>)}</tr></thead>
              <tbody>{items.map((i, n) => (
                <tr key={`${i.employeeId}-${i.name}-${n}`} className="border-t align-top" data-testid={`eligibility-row-${n}`}>
                  <td className="p-1.5 font-medium">{i.name}</td>
                  <td className="p-1.5">{i.employeeId || <span className="text-red-700">none</span>}</td>
                  <td className="p-1.5">{i.center}</td>
                  <td className="p-1.5">{i.payableDays ?? "—"}</td>
                  <td className="p-1.5 space-x-1">{i.categories.map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}</td>
                  <td className="p-1.5 text-muted-foreground">{i.reason}{i.possibleDuplicates?.length > 0 &&
                    <div>Similar: {i.possibleDuplicates.map((d) => `${d.name} (${d.employeeId})`).join(", ")}</div>}</td>
                  <td className="p-1.5 flex flex-wrap gap-1">
                    {i.categories.includes("ATTENDANCE_MISMATCH") && (
                      <Button size="sm" variant="outline" className="h-6" onClick={() => setRa(i)} data-testid={`eligibility-reassign-${n}`}>Reassign attendance</Button>)}
                    {i.employeeId && i.inPayroll && (
                      <Button size="sm" variant="outline" className="h-6" onClick={() => setRm({ employeeId: i.employeeId,
                        employeeName: i.name, center: "ALL", payableDays: i.payableDays })} data-testid={`eligibility-remove-${n}`}>Remove from Payroll</Button>)}
                    {i.employeeId && !i.categories.includes("DELETED") && (
                      <Button size="sm" variant="ghost" className="h-6 text-red-700" onClick={() => setDel({ employee_id: i.employeeId, name: i.name })}
                        data-testid={`eligibility-delete-${n}`}>Delete from Master</Button>)}
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
        </CardContent>)}
      <RemoveFromPayrollDialog open={!!rm} onClose={() => setRm(null)} token={token} month={month} target={rm} onDone={load} />
      <ReassignAttendanceDialog open={!!ra} onClose={() => setRa(null)} token={token} month={month}
        fromName={ra?.name} candidates={ra?.possibleDuplicates} onDone={load} />
      <DeleteEmployeeDialog open={!!del} onClose={() => setDel(null)} session={session} employee={del} onDeleted={load} />
    </Card>
  );
}
